import { Link } from 'react-router-dom'
import BrandLogo from './BrandLogo'
import { dashboardUrl } from '../lib/dashboardUrl'
import { openConsentManager } from '../lib/consent'

// The site footer. One column per trunk, so the two keyword territories link
// down into their own pages and only meet here and at the nav pill
// (docs/MARKETING-SITE-SPEC.md §7.4).
//
// ⛔ INTERNAL LINKS ARE <Link>, NOT marketingUrl(). This footer used to send every
// marketing link through marketingUrl(), which returns an ABSOLUTE
// https://tempdash.app/... whenever import.meta.env.PROD is set. `npm run build`
// sets it. So in any local or preview build, clicking "Overview" left the site
// you were reviewing and landed on the LIVE site: the rebuild looked like it had
// no pages behind its links, because every link went somewhere else (founder
// 2026-10-02, and they were right to call it).
//
// marketingUrl() made sense when one bundle was served from three origins and the
// dashboard rendered this footer. V2's dashboard is a separate app with its own
// chrome, so this footer only ever renders on the marketing site and an internal
// link is same-origin by definition. <Link> also gives client side navigation,
// so the footer no longer costs a full page load.
//
// dashboardUrl() stays absolute: that genuinely is a different origin.
//
// ⛔ THE LEGAL BLOCK AT THE BOTTOM IS VERBATIM AND LOAD BEARING. Do not reword,
// reorder or "tidy" it:
//   · Companies Act 2006 s.82 requires the company number, registered office and
//     place of registration on a commercial website
//   · Article 13 UK GDPR is why the ICO registration sits beside it
//   · the marketplace disclosure is what keeps the Employment Agencies Act and
//     IR35 reading unambiguous to users, crawlers and AI summaries, and it is
//     solicitor review pending
// If a line here changes, it changes in the apps' legal documents too.

const COLUMNS = [
  {
    title: 'Find work',
    links: [
      { to: '/',                        label: 'Overview' },
      { to: '/how-it-works',            label: 'How it works' },
      { to: '/getting-paid',            label: 'Getting paid' },
      { to: '/trust',                   label: 'Trust' },
      { to: '/signup?for=work',         label: 'Join TempDash' },
    ],
  },
  {
    title: 'Get cover',
    links: [
      { to: '/business',                label: 'Overview' },
      { to: '/business/how-it-works',   label: 'How it works' },
      { to: '/business/pricing',        label: 'Pricing' },
      { to: '/business/compliance',     label: 'Compliance' },
      { href: dashboardUrl('/register'),            label: 'Post a shift' },
    ],
  },
  {
    title: 'Company',
    links: [
      { to: '/about',                   label: 'About us' },
      { to: '/faq',                     label: 'Questions' },
      { to: '/status',                  label: 'Service status' },
      { to: '/legal',                   label: 'Legal' },
      { href: dashboardUrl('/login'),  label: 'Sign in' },
    ],
  },
  {
    title: 'Trust',
    links: [
      { to: '/legal/privacy',           label: 'Privacy' },
      { to: '/legal/terms',             label: 'Terms' },
      { to: '/legal/cookies',           label: 'Cookie notice' },
    ],
  },
]

export default function MarketingFooter() {
  return (
    <footer className="bg-navy px-6 py-16 text-white">
      <div className="mx-auto max-w-[1200px]">
        <div className="mb-12 flex flex-col gap-10 md:flex-row md:justify-between">
          <div>
            <BrandLogo size="lg" variant="dark" />
          </div>

          <nav aria-label="Footer" className="grid grid-cols-2 gap-x-10 gap-y-8 text-[15px] sm:grid-cols-4">
            {COLUMNS.map((col) => (
              <div key={col.title}>
                <h2 className="mb-3 text-[13px] font-semibold uppercase tracking-[0.1em] text-white">
                  {col.title}
                </h2>
                <ul className="space-y-2.5 text-navy-muted">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      {l.to
                        ? <Link to={l.to} className="transition-colors hover:text-white">{l.label}</Link>
                        : <a href={l.href} className="transition-colors hover:text-white">{l.label}</a>}
                    </li>
                  ))}
                  {/* Re-opens the consent banner so visitors can change or
                      withdraw their choice at any time (UK GDPR). Plain
                      button, no navigation: the banner lives on every page. */}
                  {col.title === 'Trust' && (
                    <li>
                      <button type="button" onClick={openConsentManager} className="transition-colors hover:text-white">
                        Cookie settings
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            ))}
          </nav>
        </div>

        <div className="flex flex-col gap-3 border-t border-white/10 pt-8 text-[13px] text-navy-muted sm:flex-row sm:items-center sm:justify-between">
          <span>© {new Date().getFullYear()} TempDash Ltd. All rights reserved.</span>
        </div>

        <div className="mx-auto max-w-[760px] space-y-3 pt-6 text-[12px] leading-relaxed text-navy-muted">
          <p>
            TempDash is a technology marketplace, not an employment agency or employment business. Dental practices engage self-employed dental professionals directly under a Contract for Services; TempDash does not employ, supply or supervise the professional.
          </p>
          <p>
            TempDash is a trading name of TempDash Ltd, a company registered in England and Wales.
          </p>
          <p>
            TempDash Ltd · Registered in England &amp; Wales · Company No. 17264372 · ICO Registration: ZC159507 · Registered office: 124 City Rd, London EC1V 2NX
          </p>
        </div>
      </div>
    </footer>
  )
}
